import {
  REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
  RealtimePostgresChangesFilter,
  SupabaseClient,
} from "@supabase/supabase-js";
import { PayloadForPostgresChangeEvent, SupabaseRealtimeChannel } from "./supabase-realtime-channel";

/**
 * This class provides an async iterable interface to listen for realtime updates from a Supabase Postgres database.
 *
 * It uses the Supabase Realtime API to listen for changes in a specific table and event type, and also applies a
 * client-side filter to the events before yielding them.
 *
 * This class also handles closing the stream when it is no longer needed, and throws any errors that occur during the
 * streaming process.
 */
export class SupabaseRealtimePostgresEventStream<
  TEvent extends REALTIME_POSTGRES_CHANGES_LISTEN_EVENT,
  TTableRow extends Record<string, unknown>,
>
  implements AsyncIterable<Omit<PayloadForPostgresChangeEvent<TEvent, TTableRow>, "errors">>, AsyncDisposable
{
  private readonly channel: SupabaseRealtimeChannel;
  private readonly eventQueue: (PayloadForPostgresChangeEvent<TEvent, TTableRow> | Error)[] = [];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private signalNewEvents: () => void = () => {};

  constructor(
    supabase: SupabaseClient,
    channelName: string,
    private readonly serverFilter: RealtimePostgresChangesFilter<TEvent>,
  ) {
    console.debug(`Creating Supabase realtime channel for event '${serverFilter.event}':`, serverFilter);
    this.channel = new SupabaseRealtimeChannel(supabase, channelName, this.enqueueEvent.bind(this));

    // Add an event listener for the specific event type we're interested in and add them to the queue
    this.channel.onPostgresChange<TEvent, TTableRow>(serverFilter, this.enqueueEvent.bind(this));
  }

  private enqueueEvent(event: PayloadForPostgresChangeEvent<TEvent, TTableRow> | Error): void {
    this.eventQueue.push(event);
    this.signalNewEvents();
  }

  public async *[Symbol.asyncIterator](): AsyncIterator<
    Omit<PayloadForPostgresChangeEvent<TEvent, TTableRow>, "errors">
  > {
    // Attempt to start listening for events
    // NOTE: This will throw an error if the subscription fails, which can be caught by the caller
    await this.channel.subscribeAsync();
    console.debug(`Supabase realtime channel subscribed for event '${this.serverFilter.event}':`, this.serverFilter);

    while (true) {
      if (this.eventQueue.length > 0) {
        // If an error is found in the queue, throw it
        const event = this.eventQueue.shift()!;
        if (event instanceof Error) throw event;

        // Otherwise, yield the event
        yield event;
      } else {
        // Otherwise, wait until new data arrives.
        await new Promise<void>((resolve) => {
          this.signalNewEvents = resolve;
        });
      }
    }
  }

  async [Symbol.asyncDispose](): Promise<void> {
    await this.channel[Symbol.asyncDispose]();
  }
}
